import type { Context, LocalizedString, ContextApplicability } from 'types/context';
import type { FilterExpression } from 'types/filters';

export const BUILT_IN_ORG_ID = '00000000-0000-0000-0000-000000000000';

export type ContextSource = 'platform' | 'org';

export interface ContextLibraryFilters {
  source?: 'all' | ContextSource;
  search?: string;
  useCaseTags?: string[];
}

export interface ContextLibraryStats {
  total: number;
  platformCount: number;
  orgCount: number;
  totalViews: number;
}

export function classifyContextSource(context: Context): ContextSource {
  return context.organization_id === BUILT_IN_ORG_ID ? 'platform' : 'org';
}

export function resolveLocalizedText(text: LocalizedString | undefined, locale: string): string {
  if (!text) return '';
  return text.translations?.[locale] ?? text.translations?.['en-US'] ?? text.default ?? '';
}

export function getAppliesToTypes(applicability: ContextApplicability | undefined): string[] {
  if (!applicability?.propertyFilters?.length) return [];
  return (applicability.propertyFilters as FilterExpression[])
    .filter((f) => f.property === 'type')
    .flatMap((f) => {
      if (Array.isArray(f.values)) return f.values.filter((v): v is string => typeof v === 'string');
      if (typeof f.value === 'string') return [f.value];
      if (Array.isArray(f.value)) return f.value.filter((v): v is string => typeof v === 'string');
      return [];
    });
}

export function composeContextFilter(
  contexts: Context[],
  filters: ContextLibraryFilters,
  locale: string
): Context[] {
  const { source = 'all', search = '', useCaseTags = [] } = filters;

  return contexts.filter((ctx) => {
    if (source !== 'all' && classifyContextSource(ctx) !== source) return false;

    if (search.trim()) {
      const query = search.toLowerCase();
      const name = resolveLocalizedText(ctx.name, locale).toLowerCase();
      const description = resolveLocalizedText(ctx.description, locale).toLowerCase();
      const systemName = ctx.system_name.toLowerCase();
      if (!name.includes(query) && !description.includes(query) && !systemName.includes(query)) {
        return false;
      }
    }

    if (useCaseTags.length > 0) {
      const tags = Array.isArray(ctx.use_case_tags) ? ctx.use_case_tags : [];
      if (!useCaseTags.some((tag) => tags.includes(tag))) return false;
    }

    return true;
  });
}

export function computeLibraryStats(contexts: Context[]): ContextLibraryStats {
  let platformCount = 0;
  let orgCount = 0;
  let totalViews = 0;
  for (const ctx of contexts) {
    if (classifyContextSource(ctx) === 'platform') {
      platformCount++;
    } else {
      orgCount++;
    }
    totalViews += ctx.viewCount ?? 0;
  }
  return { total: contexts.length, platformCount, orgCount, totalViews };
}
